import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { KpiCard } from './KpiCard';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { MessageSquare, Users, Bot, Layers } from 'lucide-react';
import type { UsageMetrics } from '@/hooks/useExecutiveUsageMetrics';
import { formatBucketLabel, Granularity } from '@/lib/timeSeries';

const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--info))', 'hsl(var(--success))', 'hsl(var(--warning))'];

const ASSISTANT_LABELS: Record<string, string> = {
  lia: 'LIA (chat)',
  lia_class: 'Clase con LIA',
  challenge: 'Laboratorio de Retos',
  prototype: 'Asistente de Prototipado',
  'change-management': 'Gestión del Cambio',
  role_redesign: 'Rediseño de Roles',
  role_task_identification: 'Identificación de Tareas',
  otro: 'Otro',
};

const label = (t: string) => ASSISTANT_LABELS[t] ?? t;

interface Props {
  data?: UsageMetrics;
  isLoading: boolean;
  granularity: Granularity;
}

export function AssistantsTab({ data, isLoading, granularity }: Props) {
  if (isLoading || !data) {
    return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-72" /></div>;
  }

  const a = data.assistants;
  const types = Array.from(new Set(a.series.map(s => s.tipo)));
  const buckets = Array.from(new Set(a.series.map(s => s.bucket))).sort();
  const series = buckets.map(b => {
    const row: Record<string, string | number> = { label: formatBucketLabel(b, granularity) };
    for (const t of types) {
      row[label(t)] = a.series.find(s => s.bucket === b && s.tipo === t)?.sesiones ?? 0;
    }
    return row;
  });

  const avgMsgs = a.totals.sesiones > 0 ? (a.totals.mensajes / a.totals.sesiones).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Sesiones de IA" value={a.totals.sesiones.toLocaleString('es-CO')} icon={<Bot className="w-4 h-4" />} accent="primary" />
        <KpiCard label="Usuarios que usan IA" value={a.totals.usuarios.toLocaleString('es-CO')} icon={<Users className="w-4 h-4" />} accent="info" />
        <KpiCard label="Mensajes enviados" value={a.totals.mensajes.toLocaleString('es-CO')} icon={<MessageSquare className="w-4 h-4" />} accent="accent" />
        <KpiCard label="Profundidad promedio" value={`${avgMsgs} msj/sesión`} icon={<Layers className="w-4 h-4" />} accent="success" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Uso por asistente</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={a.by_type.map(t => ({ ...t, name: label(t.tipo) }))} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" width={150} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="sesiones" name="Sesiones" fill="hsl(var(--primary))" radius={[0,4,4,0]} />
                <Bar dataKey="usuarios" name="Usuarios" fill="hsl(var(--accent))" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Evolución de sesiones por asistente</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
                {types.map((t, i) => (
                  <Line key={t} type="monotone" dataKey={label(t)} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Profundidad de conversación</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={a.depth} dataKey="sesiones" nameKey="rango" outerRadius={100} label>
                  {a.depth.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Organizaciones que más usan IA</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {a.orgs.map(o => (
                <div key={o.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <span className="text-sm font-medium">{o.name}</span>
                  <span className="text-xs text-muted-foreground">{o.sesiones} sesiones · {o.usuarios} usuarios</span>
                </div>
              ))}
              {a.orgs.length === 0 && <p className="text-sm text-muted-foreground">Sin datos en el periodo.</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
